import { analyticsEdgeHandlers, analyticsRpcHandlers } from './demoAnalytics';
// Mock data store for demo mode
// European fashion/sneaker theme matching existing provider types

// ── Demo identifiers ──────────────────────────────────────────────
export const DEMO_USER_ID = 'demo-user-001';
export const DEMO_MERCHANT_ID = 'demo-merchant-001';
export const DEMO_PROVIDER_ID = 'demo-provider-001';
export const DEMO_PROVIDER_2_ID = 'demo-provider-002';
export const DEMO_CONFIG_ID = 'demo-config-001';
export const DEMO_SUBSCRIPTION_ID = 'demo-sub-001';

// ── Demo user & session (matches Supabase User / Session shape) ──
export const demoUser = {
  id: DEMO_USER_ID,
  aud: 'authenticated',
  role: 'authenticated',
  email: 'demo@flowstock.app',
  email_confirmed_at: '2025-01-15T10:00:00Z',
  created_at: '2025-01-15T10:00:00Z',
  updated_at: '2025-01-15T10:00:00Z',
  app_metadata: { provider: 'google', providers: ['google'] },
  user_metadata: { full_name: 'Demo User', avatar_url: '' },
  identities: [],
};

export const demoSession = {
  access_token: 'demo-access-token',
  refresh_token: 'demo-refresh-token',
  expires_in: 3600,
  expires_at: Math.floor(Date.now() / 1000) + 3600,
  token_type: 'bearer',
  user: demoUser,
};

// ── Helpers ───────────────────────────────────────────────────────
function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString();
}
function hoursAgo(n: number): string {
  const d = new Date();
  d.setHours(d.getHours() - n);
  return d.toISOString();
}
function randomId(): string {
  return Math.random().toString(36).slice(2, 10);
}

// ── Products ──────────────────────────────────────────────────────
const productBase = [
  { title: 'Nike Air Max 90', sku: 'GS-NIKE-AM90', vendor: 'Nike', category: 'Sneakers', price: 139.99, stock: 42 },
  { title: 'Adidas Ultraboost 23', sku: 'GS-ADI-UB23', vendor: 'Adidas', category: 'Running', price: 189.99, stock: 28 },
  { title: 'New Balance 574', sku: 'GS-NB-574', vendor: 'New Balance', category: 'Lifestyle', price: 109.99, stock: 55 },
  { title: 'Puma RS-X', sku: 'GS-PUMA-RSX', vendor: 'Puma', category: 'Sneakers', price: 119.99, stock: 33 },
  { title: 'Nike Dunk Low', sku: 'GS-NIKE-DL', vendor: 'Nike', category: 'Sneakers', price: 119.99, stock: 18 },
  { title: 'Adidas Stan Smith', sku: 'GS-ADI-SS', vendor: 'Adidas', category: 'Lifestyle', price: 99.99, stock: 61 },
  { title: 'Nike Air Force 1', sku: 'GS-NIKE-AF1', vendor: 'Nike', category: 'Sneakers', price: 109.99, stock: 45 },
  { title: 'Adidas Gazelle', sku: 'GS-ADI-GAZ', vendor: 'Adidas', category: 'Lifestyle', price: 89.99, stock: 37 },
  { title: 'New Balance 2002R', sku: 'GS-NB-2002R', vendor: 'New Balance', category: 'Running', price: 159.99, stock: 22 },
  { title: 'Puma Suede Classic', sku: 'GS-PUMA-SC', vendor: 'Puma', category: 'Lifestyle', price: 79.99, stock: 48 },
  { title: 'Nike Zoom Pegasus 40', sku: 'GS-NIKE-ZP40', vendor: 'Nike', category: 'Running', price: 129.99, stock: 30 },
  { title: 'Adidas Forum Low', sku: 'GS-ADI-FL', vendor: 'Adidas', category: 'Sneakers', price: 99.99, stock: 26 },
  { title: 'New Balance 530', sku: 'GS-NB-530', vendor: 'New Balance', category: 'Lifestyle', price: 119.99, stock: 40 },
  { title: 'Nike Blazer Mid', sku: 'GS-NIKE-BM', vendor: 'Nike', category: 'Sneakers', price: 109.99, stock: 15 },
  { title: 'Adidas Samba OG', sku: 'GS-ADI-SOG', vendor: 'Adidas', category: 'Lifestyle', price: 109.99, stock: 52 },
  { title: 'Puma Palermo', sku: 'GS-PUMA-PAL', vendor: 'Puma', category: 'Lifestyle', price: 89.99, stock: 35 },
  { title: 'Nike Air Max 97', sku: 'GS-NIKE-AM97', vendor: 'Nike', category: 'Sneakers', price: 179.99, stock: 19 },
  { title: 'Adidas NMD R1', sku: 'GS-ADI-NMD', vendor: 'Adidas', category: 'Running', price: 149.99, stock: 31 },
  { title: 'New Balance 990v6', sku: 'GS-NB-990V6', vendor: 'New Balance', category: 'Running', price: 199.99, stock: 12 },
  { title: 'Nike Cortez', sku: 'GS-NIKE-COR', vendor: 'Nike', category: 'Lifestyle', price: 89.99, stock: 44 },
];

const productDescriptions = productBase.map((p, i) => ({
  id: `demo-pd-${String(i + 1).padStart(3, '0')}`,
  provider_id: DEMO_PROVIDER_ID,
  title: p.title,
  sku: p.sku,
  vendor: p.vendor,
  category: p.category,
  description: `Premium ${p.category.toLowerCase()} footwear from ${p.vendor}. Designed for comfort and style.`,
  created_at: daysAgo(60 - i),
  updated_at: daysAgo(i % 5),
}));

const sizes = ['38', '39', '40', '41', '42', '43', '44', '45', '46'];

const productVariants = productBase.flatMap((p, pi) => {
  const pdId = `demo-pd-${String(pi + 1).padStart(3, '0')}`;
  const variantSizes = sizes.slice(0, 5 + (pi % 4));
  return variantSizes.map((size, si) => ({
    id: `demo-pv-${String(pi + 1).padStart(3, '0')}-${size}`,
    product_description_id: pdId,
    provider_id: DEMO_PROVIDER_ID,
    sku: `${p.sku}-${size}`,
    size,
    price: p.price,
    compare_at_price: p.price * 1.2,
    quantity: Math.max(0, p.stock - si * 3),
    barcode: `${4000000000000 + pi * 100 + si}`,
    created_at: daysAgo(60 - pi),
    updated_at: daysAgo(pi % 5),
  }));
});

// Catalog RPC format (flattened summary per product)
const catalogProducts = productBase.map((p, i) => {
  const pdId = `demo-pd-${String(i + 1).padStart(3, '0')}`;
  const variants = productVariants.filter(v => v.product_description_id === pdId);
  return {
    product_id: pdId,
    title: p.title,
    sku: p.sku,
    vendor: p.vendor,
    photo_url: null,
    category: p.category,
    variant_count: variants.length,
    in_stock: p.stock > 0,
    lowest_price: p.price,
    total_quantity: variants.reduce((sum, v) => sum + v.quantity, 0),
  };
});

// ── Provider 2 Products (TurumPurum Italia) ──────────────────────
const provider2ProductBase = [
  // Overlapping with GoldSneakers (same titles, different SKUs/prices — some cheaper, some not)
  { title: 'Nike Air Max 90', sku: 'TP-NIKE-AM90', vendor: 'Nike', category: 'Sneakers', price: 134.99, stock: 38 },
  { title: 'Adidas Ultraboost 23', sku: 'TP-ADI-UB23', vendor: 'Adidas', category: 'Running', price: 194.99, stock: 25 },
  { title: 'New Balance 574', sku: 'TP-NB-574', vendor: 'New Balance', category: 'Lifestyle', price: 104.99, stock: 30 },
  { title: 'Puma RS-X', sku: 'TP-PUMA-RSX', vendor: 'Puma', category: 'Sneakers', price: 124.99, stock: 20 },
  { title: 'Nike Dunk Low', sku: 'TP-NIKE-DL', vendor: 'Nike', category: 'Sneakers', price: 114.99, stock: 22 },
  { title: 'Adidas Stan Smith', sku: 'TP-ADI-SS', vendor: 'Adidas', category: 'Lifestyle', price: 94.99, stock: 40 },
  { title: 'Nike Air Force 1', sku: 'TP-NIKE-AF1', vendor: 'Nike', category: 'Sneakers', price: 114.99, stock: 28 },
  { title: 'Adidas Gazelle', sku: 'TP-ADI-GAZ', vendor: 'Adidas', category: 'Lifestyle', price: 84.99, stock: 50 },
  { title: 'New Balance 2002R', sku: 'TP-NB-2002R', vendor: 'New Balance', category: 'Running', price: 154.99, stock: 18 },
  { title: 'Adidas Samba OG', sku: 'TP-ADI-SOG', vendor: 'Adidas', category: 'Lifestyle', price: 104.99, stock: 45 },
  { title: 'Nike Air Max 97', sku: 'TP-NIKE-AM97', vendor: 'Nike', category: 'Sneakers', price: 174.99, stock: 14 },
  { title: 'Adidas NMD R1', sku: 'TP-ADI-NMD', vendor: 'Adidas', category: 'Running', price: 144.99, stock: 26 },
  // Unique to TurumPurum — Italian luxury brands
  { title: 'Gucci Ace Sneaker', sku: 'TP-GUC-ACE', vendor: 'Gucci', category: 'Lifestyle', price: 620.00, stock: 8 },
  { title: 'Prada Downtown', sku: 'TP-PRA-DT', vendor: 'Prada', category: 'Sneakers', price: 890.00, stock: 5 },
  { title: 'Valentino Rockrunner', sku: 'TP-VAL-RR', vendor: 'Valentino', category: 'Sneakers', price: 750.00, stock: 10 },
  { title: 'Versace Greca', sku: 'TP-VER-GRE', vendor: 'Versace', category: 'Lifestyle', price: 595.00, stock: 12 },
  { title: 'Balenciaga Track', sku: 'TP-BAL-TRK', vendor: 'Balenciaga', category: 'Running', price: 895.00, stock: 7 },
  { title: 'Dolce & Gabbana Portofino', sku: 'TP-DG-POR', vendor: 'Dolce & Gabbana', category: 'Lifestyle', price: 545.00, stock: 15 },
];

const provider2Descriptions = provider2ProductBase.map((p, i) => ({
  id: `demo-pd2-${String(i + 1).padStart(3, '0')}`,
  provider_id: DEMO_PROVIDER_2_ID,
  title: p.title,
  sku: p.sku,
  vendor: p.vendor,
  category: p.category,
  description: `Premium ${p.category.toLowerCase()} footwear from ${p.vendor}. Designed for comfort and style.`,
  created_at: daysAgo(50 - i),
  updated_at: daysAgo(i % 4),
}));

const provider2Variants = provider2ProductBase.flatMap((p, pi) => {
  const pdId = `demo-pd2-${String(pi + 1).padStart(3, '0')}`;
  const variantSizes = sizes.slice(0, 5 + (pi % 4));
  return variantSizes.map((size, si) => ({
    id: `demo-pv2-${String(pi + 1).padStart(3, '0')}-${size}`,
    product_description_id: pdId,
    provider_id: DEMO_PROVIDER_2_ID,
    sku: `${p.sku}-${size}`,
    size,
    price: p.price,
    compare_at_price: p.price * 1.2,
    quantity: Math.max(0, p.stock - si * 3),
    barcode: `${5000000000000 + pi * 100 + si}`,
    created_at: daysAgo(50 - pi),
    updated_at: daysAgo(pi % 4),
  }));
});

const provider2CatalogProducts = provider2ProductBase.map((p, i) => {
  const pdId = `demo-pd2-${String(i + 1).padStart(3, '0')}`;
  const variants = provider2Variants.filter(v => v.product_description_id === pdId);
  return {
    product_id: pdId,
    title: p.title,
    sku: p.sku,
    vendor: p.vendor,
    photo_url: null,
    category: p.category,
    variant_count: variants.length,
    in_stock: p.stock > 0,
    lowest_price: p.price,
    total_quantity: variants.reduce((sum, v) => sum + v.quantity, 0),
  };
});

// Combined catalog for cross-provider search
const allCatalogProducts = [
  ...catalogProducts.map(p => ({ ...p, provider_id: DEMO_PROVIDER_ID, provider_name: 'GoldSneakers EU' })),
  ...provider2CatalogProducts.map(p => ({ ...p, provider_id: DEMO_PROVIDER_2_ID, provider_name: 'TurumPurum Italia' })),
];

// ── Update queue ──────────────────────────────────────────────────
const updateTypes = ['stock_update', 'price_update', 'title_update', 'new_product', 'image_update'];
const updateQueue = Array.from({ length: 15 }, (_, i) => {
  const pd = productDescriptions[i % productDescriptions.length];
  return {
    id: `demo-uq-${String(i + 1).padStart(3, '0')}`,
    provider_id: DEMO_PROVIDER_ID,
    product_description_id: pd.id,
    product_variant_id: i < 10 ? productVariants.find(v => v.product_description_id === pd.id)?.id : null,
    update_type: updateTypes[i % updateTypes.length],
    priority: i < 5 ? 1 : 2,
    update_data: { field: updateTypes[i % updateTypes.length], old_value: 'previous', new_value: 'updated' },
    created_at: hoursAgo(i * 2 + 1),
    completed_at: i < 12 ? hoursAgo(i * 2) : null,
  };
});

// ── Orders ────────────────────────────────────────────────────────
const orderStatuses = ['pending', 'processing', 'shipped', 'delivered', 'delivered'];
const customers = [
  { name: 'Max Müller', email: 'max.mueller@gmail.com', city: 'Berlin', country: 'Germany' },
  { name: 'Sophie Dupont', email: 'sophie.dupont@outlook.fr', city: 'Paris', country: 'France' },
  { name: 'Jan de Vries', email: 'jan.devries@gmail.com', city: 'Amsterdam', country: 'Netherlands' },
  { name: 'Maria García', email: 'maria.garcia@yahoo.es', city: 'Madrid', country: 'Spain' },
  { name: 'Luca Rossi', email: 'luca.rossi@gmail.com', city: 'Milan', country: 'Italy' },
  { name: 'Anna Kowalska', email: 'anna.k@wp.pl', city: 'Warsaw', country: 'Poland' },
  { name: 'Erik Johansson', email: 'erik.j@outlook.se', city: 'Stockholm', country: 'Sweden' },
  { name: 'Elena Petrova', email: 'elena.p@mail.com', city: 'Prague', country: 'Czech Republic' },
  { name: 'Thomas Weber', email: 'thomas.w@gmx.de', city: 'Munich', country: 'Germany' },
  { name: 'Claire Martin', email: 'claire.m@orange.fr', city: 'Lyon', country: 'France' },
];

const orders = customers.map((c, i) => {
  const lineItemCount = 1 + (i % 3);
  const lineItems = Array.from({ length: lineItemCount }, (_, li) => {
    const pd = productDescriptions[(i * 3 + li) % productDescriptions.length];
    const variant = productVariants.find(v => v.product_description_id === pd.id);
    return {
      title: pd.title,
      sku: variant?.sku || pd.sku,
      quantity: 1 + (li % 2),
      price: productBase[(i * 3 + li) % productBase.length].price,
      variant_title: variant?.size || '42',
    };
  });
  const total = lineItems.reduce((s, li) => s + li.price * li.quantity, 0);
  const status = orderStatuses[i % orderStatuses.length];

  return {
    order_id: `demo-order-${String(i + 1).padStart(3, '0')}`,
    merchant_id: DEMO_MERCHANT_ID,
    merchant_name: 'Urban Style Berlin',
    merchant_shop_name: 'Urban Style Berlin',
    merchant_shop_domain: 'urbanstyle-berlin.myshopify.com',
    merchant_email: 'info@urbanstyle-berlin.com',
    provider_id: DEMO_PROVIDER_ID,
    provider_name: 'GoldSneakers EU',
    shopify_order_id: 5000000000 + i,
    order_number: `USB-${1001 + i}`,
    number: 1001 + i,
    customer_email: c.email,
    customer_phone: '+49 30 1234567',
    customer_id: 7000000 + i,
    customer_data: { first_name: c.name.split(' ')[0], last_name: c.name.split(' ').slice(1).join(' ') },
    current_total_price_amount: total,
    current_subtotal_price_amount: total * 0.84,
    currency_code: 'EUR',
    display_financial_status: 'PAID',
    display_fulfillment_status: status === 'delivered' ? 'FULFILLED' : status === 'shipped' ? 'IN_TRANSIT' : 'UNFULFILLED',
    line_items: lineItems,
    line_items_count: lineItemCount,
    shipping_address: {
      first_name: c.name.split(' ')[0],
      last_name: c.name.split(' ').slice(1).join(' '),
      address1: `${c.city} Str. ${10 + i}`,
      city: c.city,
      province: '',
      country: c.country,
      zip: `${10000 + i * 111}`,
      country_code: c.country.slice(0, 2).toUpperCase(),
    },
    billing_address: null,
    fulfillment_provider: '',
    external_order_id: '',
    fulfillment_tracking_url: status === 'shipped' || status === 'delivered' ? `https://tracking.example.com/${randomId()}` : '',
    latest_provider_update: status !== 'pending' ? {
      status,
      tracking_number: status === 'shipped' || status === 'delivered' ? `TR${randomId().toUpperCase()}` : undefined,
      tracking_url: status === 'shipped' || status === 'delivered' ? `https://tracking.example.com/${randomId()}` : undefined,
      carrier: status === 'shipped' || status === 'delivered' ? 'DHL Express' : undefined,
      notes: '',
      updated_at: hoursAgo(i * 6),
    } : null,
    tags: [status],
    created_at: daysAgo(i * 2 + 1),
    updated_at: hoursAgo(i * 6),
  };
});

// ── Connection requests ───────────────────────────────────────────
const connectionRequests = [
  {
    merchant_id: 'demo-merchant-pending',
    merchant_name: 'Sneaker Spot Amsterdam',
    shop_domain: 'sneaker-spot-ams.myshopify.com',
    stock_provider: true,
    orders_provider: false,
    connection_request_id: 'demo-cr-001',
    connection_status: 'pending',
    requested_at: daysAgo(2),
    reviewed_at: null,
    addresses: null,
    capabilities_requested: { stock: true, orders: false },
    merchant_credentials: null,
    auto_order_config: null,
    rejection_reason: null,
    notes: 'Interested in your sneaker catalog for our Amsterdam store.',
    credential_type: 'api_key' as const,
  },
  {
    merchant_id: DEMO_MERCHANT_ID,
    merchant_name: 'Urban Style Berlin',
    shop_domain: 'urbanstyle-berlin.myshopify.com',
    stock_provider: true,
    orders_provider: true,
    connection_request_id: 'demo-cr-002',
    connection_status: 'approved',
    requested_at: daysAgo(30),
    reviewed_at: daysAgo(28),
    addresses: null,
    capabilities_requested: { stock: true, orders: true },
    merchant_credentials: { api_key: '••••••••' },
    auto_order_config: null,
    rejection_reason: null,
    notes: null,
    credential_type: 'api_key' as const,
  },
  {
    merchant_id: 'demo-merchant-connected',
    merchant_name: 'Fashion Hub Paris',
    shop_domain: 'fashion-hub-paris.myshopify.com',
    stock_provider: true,
    orders_provider: true,
    connection_request_id: 'demo-cr-003',
    connection_status: 'connected',
    requested_at: daysAgo(60),
    reviewed_at: daysAgo(58),
    addresses: null,
    capabilities_requested: { stock: true, orders: true },
    merchant_credentials: { api_key: '••••••••' },
    auto_order_config: { auto_fulfill: true },
    rejection_reason: null,
    notes: null,
    credential_type: 'api_key' as const,
  },
];

// ── In-memory data store ──────────────────────────────────────────
type DataStore = Record<string, any[]>;

const initialData: DataStore = {
  merchants: [
    {
      id: DEMO_MERCHANT_ID,
      email: 'info@urbanstyle-berlin.com',
      name: 'Urban Style Berlin',
      shop_name: 'Urban Style Berlin',
      shop_domain: 'urbanstyle-berlin.myshopify.com',
      is_active: true,
      status: 'active',
      login_token: null,
      user_config_id: DEMO_CONFIG_ID,
      webhook_url: null,
    },
  ],
  merchant_config: [
    {
      id: DEMO_CONFIG_ID,
      merchant_id: DEMO_MERCHANT_ID,
      client_name: 'Urban Style Berlin',
      platform: 'shopify',
      created_at: daysAgo(90),
      updated_at: daysAgo(1),
      business_info: { company_name: 'Urban Style GmbH', country: 'DE' },
      config_object: {
        client_name: 'Urban Style Berlin',
        platform: {
          shopify: {
            shop_domain: 'urbanstyle-berlin.myshopify.com',
            access_token: '••••••••',
            location_id: 'gid://shopify/Location/12345',
          },
        },
        business_rules: {
          stock_providers: [DEMO_PROVIDER_ID],
          variant_sku_format: 'base_and_size',
          update_schema: {
            product: { title: true, brand: true, image: true, category: true, productType: true },
            variant: { price: true, stock: true },
            clientConfig: {
              standardTags: ['sneakers', 'footwear', 'fashion'],
              standardDescription: true,
              useCategoryTags: true,
              newProductStatus: 'ACTIVE',
            },
          },
        },
        pricing: {
          price_tiers: [
            { min: 0, max: 100, method: 'multiply', multiplier: 2.0 },
            { min: 100, max: 200, method: 'multiply', multiplier: 1.8 },
            { min: 200, max: 999, method: 'multiply', multiplier: 1.6 },
          ],
          vat_rate: 19,
          delivery_fee: 0,
          rounding_rules: { round_to: 'ending_99' },
        },
        branding: { standard_tags: ['UrbanStyle', 'Berlin'] },
      },
    },
  ],
  providers: [
    {
      id: DEMO_PROVIDER_ID,
      name: 'GoldSneakers EU',
      provider_type: 'goldsneakers',
      is_active: true,
      merchant_id: null,
    },
    {
      id: DEMO_PROVIDER_2_ID,
      name: 'TurumPurum Italia',
      provider_type: 'turumpurum',
      is_active: true,
      merchant_id: null,
    },
  ],
  auth_users_shops: [
    {
      auth_user_id: DEMO_USER_ID,
      shop_id: DEMO_MERCHANT_ID,
      last_access_at: new Date().toISOString(),
      merchants: {
        id: DEMO_MERCHANT_ID,
        name: 'Urban Style Berlin',
        shop_name: 'Urban Style Berlin',
        shop_domain: 'urbanstyle-berlin.myshopify.com',
        merchant_config: { client_name: 'Urban Style Berlin' },
      },
    },
  ],
  auth_users_providers: [
    {
      auth_user_id: DEMO_USER_ID,
      provider_id: DEMO_PROVIDER_ID,
      last_access_at: new Date().toISOString(),
      providers: { id: DEMO_PROVIDER_ID, name: 'GoldSneakers EU' },
    },
  ],
  merchant_provider_map: [
    {
      merchant_id: DEMO_MERCHANT_ID,
      provider_id: DEMO_PROVIDER_ID,
      stock_provider: true,
      orders_provider: true,
    },
    {
      merchant_id: DEMO_MERCHANT_ID,
      provider_id: DEMO_PROVIDER_2_ID,
      stock_provider: true,
      orders_provider: true,
    },
  ],
  subscriptions: [
    {
      id: DEMO_SUBSCRIPTION_ID,
      merchant_id: DEMO_MERCHANT_ID,
      provider_id: null,
      plan_id: 'standard_monthly',
      status: 'active',
      current_period_end: new Date(Date.now() + 30 * 86400000).toISOString(),
      created_at: daysAgo(60),
    },
    {
      id: 'demo-sub-provider-001',
      merchant_id: null,
      provider_id: DEMO_PROVIDER_ID,
      plan_id: 'provider_starter_monthly',
      status: 'active',
      current_period_end: new Date(Date.now() + 30 * 86400000).toISOString(),
      created_at: daysAgo(60),
    },
  ],
  product_descriptions: [...productDescriptions, ...provider2Descriptions],
  product_variants: [...productVariants, ...provider2Variants],
  update_queue: updateQueue,
  provider_orders_view: orders,
  connection_requests: connectionRequests,
};

// Deep clone for reset capability
function cloneStore(src: DataStore): DataStore {
  return JSON.parse(JSON.stringify(src));
}

let store: DataStore = cloneStore(initialData);

export function getData(table: string): any[] {
  return store[table] || [];
}

export function setData(table: string, data: any[]): void {
  store[table] = data;
}

export function resetData(): void {
  store = cloneStore(initialData);
}

// ── RPC handlers ──────────────────────────────────────────────────
export const rpcHandlers: Record<string, (params: any) => any> = {
  ...analyticsRpcHandlers,
  get_provider_catalog: (params: any) => {
    let items = [...catalogProducts];
    if (params.p_search) {
      const q = params.p_search.toLowerCase();
      items = items.filter(p =>
        p.title.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q)
      );
    }
    if (params.p_category) {
      items = items.filter(p => p.category === params.p_category);
    }
    if (params.p_vendor) {
      items = items.filter(p => p.vendor === params.p_vendor);
    }
    if (params.p_stock_status === 'in_stock') {
      items = items.filter(p => p.in_stock);
    } else if (params.p_stock_status === 'out_of_stock') {
      items = items.filter(p => !p.in_stock);
    }
    const total = items.length;
    const offset = params.p_offset || 0;
    const limit = params.p_limit || 20;
    items = items.slice(offset, offset + limit);
    // The RPC returns items with a total_count field on each row
    return items.map(item => ({ ...item, total_count: total }));
  },

  get_provider_merchants: (params: any) => {
    return connectionRequests.filter(
      cr => params.p_provider_id === DEMO_PROVIDER_ID || !params.p_provider_id
    );
  },

  toggle_merchant_sync: (_params: any) => {
    return { success: true };
  },

  set_provider_selection: (_params: any) => {
    return { success: true };
  },

  search_all_products: (params: any) => {
    let items = [...allCatalogProducts];
    if (params.p_search) {
      const q = params.p_search.toLowerCase();
      items = items.filter(p =>
        p.title.toLowerCase().includes(q) ||
        p.sku.toLowerCase().includes(q) ||
        p.vendor.toLowerCase().includes(q)
      );
    }
    if (params.p_category) {
      items = items.filter(p => p.category === params.p_category);
    }
    if (params.p_vendor) {
      items = items.filter(p => p.vendor === params.p_vendor);
    }
    // Sort by provider name then title
    items.sort((a, b) => a.provider_name.localeCompare(b.provider_name) || a.title.localeCompare(b.title));
    const total = items.length;
    const offset = params.p_offset || 0;
    const limit = params.p_limit || 50;
    items = items.slice(offset, offset + limit);
    return items.map(item => ({ ...item, total_count: total }));
  },

  get_all_product_filter_options: (_params: any) => {
    const categories = Array.from(new Set(allCatalogProducts.map(p => p.category).filter(Boolean))).sort();
    const vendors = Array.from(new Set(allCatalogProducts.map(p => p.vendor).filter(Boolean))).sort();
    return [{ categories, vendors }];
  },

  get_merged_inventory: (_params: any) => {
    // Group all products by title+vendor across providers
    const allDescriptions = getData('product_descriptions');
    const allVariants = getData('product_variants');

    const grouped: Record<string, any> = {};

    for (const pd of allDescriptions) {
      const key = `${pd.title}|||${pd.vendor}`;
      if (!grouped[key]) {
        grouped[key] = {
          title: pd.title,
          vendor: pd.vendor,
          category: pd.category,
          providers: [],
        };
      }
      const variants = allVariants.filter((v: any) => v.product_description_id === pd.id);
      const providerName = pd.provider_id === DEMO_PROVIDER_ID ? 'GoldSneakers EU' : 'TurumPurum Italia';
      grouped[key].providers.push({
        provider_id: pd.provider_id,
        provider_name: providerName,
        sku: pd.sku,
        variants: variants.map((v: any) => ({
          size: v.size,
          price: v.price,
          quantity: v.quantity,
        })),
      });
    }

    return Object.values(grouped).map((product: any) => {
      // Collect all sizes across all providers
      const allSizes = new Map<string, { cheapest_price: number; total_quantity: number; cheapest_provider: string; providers: any[] }>();

      for (const prov of product.providers) {
        for (const v of prov.variants) {
          const existing = allSizes.get(v.size);
          const provEntry = { provider_id: prov.provider_id, provider_name: prov.provider_name, price: v.price, quantity: v.quantity };
          if (!existing) {
            allSizes.set(v.size, {
              cheapest_price: v.price,
              total_quantity: v.quantity,
              cheapest_provider: prov.provider_name,
              providers: [provEntry],
            });
          } else {
            existing.total_quantity += v.quantity;
            existing.providers.push(provEntry);
            if (v.price < existing.cheapest_price) {
              existing.cheapest_price = v.price;
              existing.cheapest_provider = prov.provider_name;
            }
          }
        }
      }

      const mergedSizes = Array.from(allSizes.entries())
        .map(([size, data]) => ({ size, ...data }))
        .sort((a, b) => parseInt(a.size) - parseInt(b.size));

      const cheapestPrice = mergedSizes.length > 0
        ? Math.min(...mergedSizes.map(s => s.cheapest_price))
        : 0;

      return {
        title: product.title,
        vendor: product.vendor,
        category: product.category,
        provider_count: product.providers.length,
        providers: product.providers.map((p: any) => ({
          provider_id: p.provider_id,
          provider_name: p.provider_name,
          sku: p.sku,
          lowest_price: Math.min(...p.variants.map((v: any) => v.price)),
          total_stock: p.variants.reduce((s: number, v: any) => s + v.quantity, 0),
        })),
        merged_sizes: mergedSizes,
        cheapest_price: cheapestPrice,
        total_stock: mergedSizes.reduce((s, sz) => s + sz.total_quantity, 0),
      };
    });
  },

  upsert_merchant_business_info: (params: any) => {
    // Update the merchant_config business_info for the given merchant
    const configs = getData('merchant_config');
    const idx = configs.findIndex((c: any) => c.merchant_id === params.p_merchant_id);
    if (idx >= 0) {
      configs[idx] = { ...configs[idx], business_info: params.p_business_info };
      setData('merchant_config', configs);
    }
    return { success: true };
  },
};

// ── Edge function handlers ────────────────────────────────────────
export const edgeFunctionHandlers: Record<string, (body: any) => any> = {
  ...analyticsEdgeHandlers,
  'provider-orders': (body: any) => {
    let filtered = [...orders];
    if (body?.merchant_id) {
      filtered = filtered.filter(o => o.merchant_id === body.merchant_id);
    }
    if (body?.status) {
      filtered = filtered.filter(o => o.tags.includes(body.status));
    }
    const stats = {
      total_orders: orders.length,
      pending_orders: orders.filter(o => o.tags.includes('pending')).length,
      processing_orders: orders.filter(o => o.tags.includes('processing')).length,
      shipped_orders: orders.filter(o => o.tags.includes('shipped')).length,
      delivered_orders: orders.filter(o => o.tags.includes('delivered')).length,
      total_revenue: orders.reduce((s, o) => s + o.current_total_price_amount, 0),
      avg_order_value: orders.reduce((s, o) => s + o.current_total_price_amount, 0) / orders.length,
    };
    return { orders: filtered, stats, total_count: filtered.length };
  },

  'review-connection-request': (body: any) => {
    return { success: true, request_id: body?.request_id, action: body?.action };
  },

  'waitlist-signup': (_body: any) => {
    return { success: true };
  },

  'test-shopify-connection': (_body: any) => {
    return [
      {
        id: 'gid://shopify/Location/demo-loc-001',
        name: 'FLOWSTOCK Warehouse',
        address: {
          address1: 'Demo Street 1',
          city: 'Berlin',
          province: '',
          country: 'Germany',
          zip: '10115',
        },
      },
    ];
  },

  'deploy_merchant': (body: any) => {
    const newId = `demo-merchant-${randomId()}`;
    // Add the new merchant to the in-memory store
    const merchants = getData('merchants');
    merchants.push({
      id: newId,
      email: body?.user_email || 'onboarding@flowstock.app',
      name: body?.merchant_name || 'New Demo Merchant',
      shop_name: body?.merchant_name || 'New Demo Merchant',
      shop_domain: body?.shopify_shop_domain || 'demo.myshopify.com',
      is_active: false,
      status: 'active',
      login_token: null,
      user_config_id: null,
      webhook_url: null,
    });
    setData('merchants', merchants);

    // Create merchant_config entry
    const configs = getData('merchant_config');
    const configId = `demo-config-${randomId()}`;
    configs.push({
      id: configId,
      merchant_id: newId,
      client_name: body?.merchant_name || 'New Demo Merchant',
      platform: 'shopify',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      business_info: null,
      config_object: {},
    });
    setData('merchant_config', configs);

    // Create subscription
    const subs = getData('subscriptions');
    subs.push({
      id: `demo-sub-${randomId()}`,
      merchant_id: newId,
      provider_id: null,
      plan_id: 'standard_monthly',
      status: 'trialing',
      current_period_end: new Date(Date.now() + 7 * 86400000).toISOString(),
      created_at: new Date().toISOString(),
    });
    setData('subscriptions', subs);

    // Link user to shop
    const userShops = getData('auth_users_shops');
    userShops.push({
      auth_user_id: DEMO_USER_ID,
      shop_id: newId,
      last_access_at: new Date().toISOString(),
      merchants: {
        id: newId,
        name: body?.merchant_name || 'New Demo Merchant',
        shop_name: body?.merchant_name || 'New Demo Merchant',
        shop_domain: body?.shopify_shop_domain || 'demo.myshopify.com',
        merchant_config: { client_name: body?.merchant_name || 'New Demo Merchant' },
      },
    });
    setData('auth_users_shops', userShops);

    return { merchant_id: newId };
  },

  'shopify-bulk-products-start': (_body: any) => {
    return { success: true, job_id: `demo-job-${randomId()}` };
  },

  'connect-provider': (_body: any) => {
    return { success: true };
  },
};
